import { useParams, useNavigate } from "react-router";
import { useState } from "react";
import { useLayoutContext } from "@/components/Layout";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { ErrorState } from "@/components/ErrorState";
import { StatusBadge } from "@/components/StatusBadge";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  useRoutingPolicyDetail,
  useActivatePolicy,
} from "@/api/hooks/use-routing";

export function RoutingPolicyDetail() {
  const { policyId } = useParams<{ policyId: string }>();
  const { tenantId } = useLayoutContext();
  const navigate = useNavigate();

  const detail = useRoutingPolicyDetail(policyId ?? "");
  const activate = useActivatePolicy();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  if (detail.isLoading) {
    return <LoadingSkeleton cards={0} rows={8} />;
  }

  if (detail.isError) {
    return (
      <ErrorState
        message="Failed to load policy details"
        onRetry={() => detail.refetch()}
      />
    );
  }

  const d = detail.data;
  if (!d) return null;

  function handleActivate() {
    if (!policyId || !tenantId) return;
    activate.mutate(
      { policyId, tenantId },
      {
        onSuccess: () => {
          setConfirmOpen(false);
          setFeedback({ type: "success", message: "Policy activated successfully." });
          setTimeout(() => setFeedback(null), 3000);
        },
        onError: (err) => {
          setConfirmOpen(false);
          setFeedback({
            type: "error",
            message: err instanceof Error ? err.message : "Failed to activate policy",
          });
          setTimeout(() => setFeedback(null), 5000);
        },
      }
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/routing")}
          className="text-fg-muted hover:text-fg transition-colors text-sm"
        >
          &larr; Back
        </button>
        <h1 className="text-xl font-semibold text-fg">{d.name}</h1>
        <StatusBadge status={d.status} />
      </div>

      {/* Feedback */}
      {feedback && (
        <div
          role={feedback.type === "success" ? "status" : "alert"}
          className={`rounded-md border px-4 py-2 text-sm ${
            feedback.type === "success"
              ? "border-safe-green/30 bg-safe-green/10 text-safe-green"
              : "border-block-red/30 bg-block-red/10 text-block-red"
          }`}
        >
          {feedback.message}
        </div>
      )}

      {/* Metadata */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div>
          <p className="text-xs font-medium text-fg-muted uppercase tracking-wider">
            Policy ID
          </p>
          <p className="mt-1 font-mono text-sm text-fg break-all">
            {d.policy_id}
          </p>
        </div>
        <div>
          <p className="text-xs font-medium text-fg-muted uppercase tracking-wider">
            Tenant ID
          </p>
          <p className="mt-1 font-mono text-sm text-fg break-all">
            {d.tenant_id}
          </p>
        </div>
        <div>
          <p className="text-xs font-medium text-fg-muted uppercase tracking-wider">
            Version
          </p>
          <p className="mt-1 font-mono text-sm text-fg">{d.version}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-fg-muted uppercase tracking-wider">
            Status
          </p>
          <div className="mt-1">
            <StatusBadge status={d.status} />
          </div>
        </div>
      </div>

      {/* Activate button */}
      {d.status !== "active" && (
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          className="rounded-md bg-electric-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-electric-blue/80 transition-colors"
        >
          Activate This Policy
        </button>
      )}

      {/* Confirmation dialog */}
      {confirmOpen && (
        <ConfirmDialog
          title="Activate Policy"
          message={`This will activate \u201c${d.name}\u201d and deactivate the currently active policy for this tenant. Continue?`}
          confirmLabel="Activate"
          isPending={activate.isPending}
          onConfirm={handleActivate}
          onCancel={() => setConfirmOpen(false)}
          variant="primary"
        />
      )}

      {/* Policy JSON */}
      <section>
        <h2 className="text-sm font-medium text-fg-muted uppercase tracking-wider mb-3">
          Policy Configuration
        </h2>
        <pre className="overflow-auto rounded-lg border border-line-subtle bg-canvas p-4 font-mono text-sm text-fg max-h-[600px]">
          {JSON.stringify(d.policy_json, null, 2)}
        </pre>
      </section>
    </div>
  );
}
